import { useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiUrl } from "../api/client";
import { KbEntryEditor } from "./kb-entry-editor";
import { useAuth } from "../auth/AuthContext";
import { RiskBadge } from "./risk-badge";
import { FormError, Modal } from "./admin-ui";
import type { RiskLevel } from "../api/types";

/**
 * Slice 8.8 — Cryptography KB admin browser.
 *
 * Read-only view of the platform's bundled Cryptography Knowledge Base.
 * Shows the active KB version, lets the user inspect each table, and
 * displays each row's source citation. This is what reviewers use to
 * validate the bundled KB against authoritative documents (NIST, BSI,
 * IETF) before the platform ships to a customer.
 *
 * Customer-defined extensions (kb_origin='Tenant') will be editable in
 * Slice 9 when the tenancy/auth layer lands; for v0.8 every customer
 * sees the bundled v1.0 content as-is.
 */

type KbTable =
  | "kb_canonical_algorithm"
  | "kb_algorithm_alias"
  | "kb_keys"
  | "kb_cert_signature_algm"
  | "kb_cert_hashing_algm"
  | "kb_cert_public_key_algm"
  | "kb_protocol"
  | "kb_cipher_suite"
  | "kb_library"
  | "kb_store"
  | "kb_signature";

const TABLES: { key: KbTable; label: string; hint: string }[] = [
  {
    key: "kb_canonical_algorithm",
    label: "Canonical algorithms",
    hint: "The names QRAM recognises",
  },
  {
    key: "kb_algorithm_alias",
    label: "Algorithm aliases",
    hint: "Customer-import name variants",
  },
  { key: "kb_keys", label: "Keys", hint: "Risk per (algorithm, key size)" },
  {
    key: "kb_cert_signature_algm",
    label: "Cert signature algorithms",
    hint: "X.509 sigAlg classifications",
  },
  {
    key: "kb_cert_hashing_algm",
    label: "Cert hashing algorithms",
    hint: "X.509 hashAlg classifications",
  },
  {
    key: "kb_cert_public_key_algm",
    label: "Cert public-key algorithms",
    hint: "X.509 pubKeyAlg classifications",
  },
  {
    key: "kb_protocol",
    label: "Protocols",
    hint: "TLS, SSH, IPsec, S/MIME, …",
  },
  {
    key: "kb_cipher_suite",
    label: "Cipher suites",
    hint: "Concrete cipher suites by IANA name",
  },
  {
    key: "kb_library",
    label: "Libraries",
    hint: "OpenSSL, BoringSSL, mbedTLS, …",
  },
  {
    key: "kb_store",
    label: "Key stores",
    hint: "HSM, KMS, file, JCEKS, PKCS#12, …",
  },
  {
    key: "kb_signature",
    label: "Signatures",
    hint: "JWT, code signing, document, …",
  },
];

interface KbVersion {
  id: string;
  version: string;
  published_at: string;
  is_current: number;
  source_summary: string;
  notes: string | null;
}

interface ListResponse<T> {
  data: T[];
  pagination: { total: number; limit: number; offset: number };
}

export function CryptographyKbAdmin() {
  const [activeTable, setActiveTable] = useState<KbTable>(
    "kb_canonical_algorithm",
  );

  const versionQ = useQuery<{ data: KbVersion | null }>({
    queryKey: ["kb-version"],
    queryFn: async () => {
      const res = await fetch(apiUrl("/admin/cryptography-kb/version"), {
        credentials: "same-origin",
      });
      if (!res.ok)
        throw new ApiError(res.status, "Failed to load KB version", null);
      return res.json();
    },
  });

  const v = versionQ.data?.data;

  return (
    <div className="space-y-4">
      <div className="border border-ink-200 rounded p-4 bg-ink-50">
        <h3 className="font-display text-lg text-ink mb-2">
          Cryptography Knowledge Base
        </h3>
        {versionQ.isLoading ? (
          <div className="text-sm text-ink-500 italic">
            Loading version info…
          </div>
        ) : v ? (
          <div className="text-sm space-y-1">
            <div>
              <span className="text-ink-500">Active version:</span>{" "}
              <span className="font-mono font-medium">{v.version}</span>
            </div>
            <div>
              <span className="text-ink-500">Published:</span>{" "}
              <span className="font-mono text-xs">
                {new Date(v.published_at).toISOString().split("T")[0]}
              </span>
            </div>
            <div>
              <span className="text-ink-500">Sources:</span>{" "}
              <span className="text-ink-700 text-xs">{v.source_summary}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-risk-critical">No KB version found</div>
        )}
      </div>

      <div className="border-b border-ink-200">
        <nav className="flex gap-1 -mb-px overflow-x-auto pb-px">
          {TABLES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTable(t.key)}
              className={`px-3 py-2 text-sm border-b-2 whitespace-nowrap transition-colors ${
                activeTable === t.key
                  ? "border-accent text-ink font-medium"
                  : "border-transparent text-ink-500 hover:text-ink"
              }`}
              title={t.hint}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <KbTableView table={activeTable} />

      <p className="text-xs text-ink-500 italic">
        Customer extensions to the KB (kb_origin = Tenant) will be editable in
        v0.9. For v0.8, the bundled System rows are read-only here. To extend
        the KB, contact AvinyaSQ Support with the algorithm names and citations
        you need added.
      </p>
    </div>
  );
}

/**
 * A short human label for a KB row, without needing per-table knowledge:
 * prefer the most name-like column present, else fall back to the id.
 */
function describeRow(r: Record<string, unknown>): string {
  for (const key of [
    "canonical_name", "alias_text", "iana_name", "name",
    "protocol_family", "store_type", "signature_purpose",
  ]) {
    const v = r[key];
    if (typeof v === "string" && v) {
      const extra = r["protocol_version"] ?? r["version_pattern"] ?? r["key_size"] ?? r["curve_name"];
      return extra ? `${v} · ${String(extra)}` : v;
    }
  }
  const ca = r["canonical_name"] ?? r["canonical_algorithm_id"];
  return ca ? String(ca) : String(r["id"] ?? "entry");
}

function KbTableView({ table }: { table: KbTable }) {
  const { roles } = useAuth();
  // Writes are super_user only; admins keep read access.
  const canEdit = roles.includes("super_user");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Record<string, unknown> | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const del = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(apiUrl(`/admin/cryptography-kb/${table}/${id}`), {
        method: "DELETE",
        credentials: "same-origin",
      });
      // 204 has no body; only parse one when the request failed.
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new ApiError(res.status, body?.error?.message ?? `HTTP ${res.status}`, body);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kb-table", table] });
      setConfirmDelete(null);
      setDeleteError(null);
    },
    // The server refuses a delete when other rows still reference the entry;
    // surface that message rather than a generic failure.
    onError: (e) => setDeleteError((e as Error).message),
  });

  const q = useQuery<ListResponse<Record<string, unknown>>>({
    queryKey: ["kb-table", table],
    queryFn: async () => {
      const res = await fetch(
        apiUrl(`/admin/cryptography-kb/${table}?limit=500`),
        { credentials: "same-origin" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new ApiError(
          res.status,
          body?.error?.message ?? `HTTP ${res.status}`,
          body,
        );
      }
      return res.json();
    },
  });

  if (q.isLoading)
    return <div className="text-sm text-ink-500 italic py-4">Loading…</div>;
  if (q.isError) return <FormError message={(q.error as Error).message} />;

  const rows = q.data?.data ?? [];
  const total = q.data?.pagination.total ?? 0;

  // kb_origin='Tenant' marks entries this organisation added. Only those are
  // editable — the bundled System content is immutable (enforced server-side).
  const tenantRows = rows.filter((r) => r['kb_origin'] === 'Tenant');

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-ink-500">
          Showing {rows.length} of {total} entries
          {tenantRows.length > 0 && <> · {tenantRows.length} added by you</>}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs bg-accent text-white px-3 py-1.5 rounded hover:bg-accent-hover"
          >
            + Add entry
          </button>
        )}
      </div>

      {confirmDelete && (
        <Modal
          open
          title="Delete KB entry"
          onClose={() => { setConfirmDelete(null); setDeleteError(null); }}
        >
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              Delete <strong>{describeRow(confirmDelete)}</strong> from{" "}
              <span className="font-mono text-xs">{table}</span>?
            </p>
            <p className="text-xs text-ink-500">
              This cannot be undone. Assessment results already scored against this
              entry keep the values they were given — they are not recalculated by
              deleting it. If the entry is still referenced anywhere, the delete will
              be refused rather than cascading.
            </p>
            {deleteError && <FormError message={deleteError} />}
            <div className="flex items-center justify-between pt-2 border-t border-ink-100">
              <button
                type="button"
                onClick={() => { setConfirmDelete(null); setDeleteError(null); }}
                className="text-sm text-ink-500 hover:text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => del.mutate(String(confirmDelete["id"]))}
                className="text-sm bg-risk-critical text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50"
              >
                {del.isPending ? "Deleting…" : "Delete entry"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {adding && <KbEntryEditor table={table} onClose={() => setAdding(false)} />}
      {editing && (
        <KbEntryEditor table={table} row={editing} onClose={() => setEditing(null)} />
      )}

      {rows.length === 0 && (
        <div className="text-sm text-ink-500 italic py-4">No rows.</div>
      )}
      <div className="border border-ink-200 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
              <KbTableHeader table={table} showActions={canEdit} />
            </thead>
            <tbody className="divide-y divide-ink-100">
              {rows.map((row, i) => (
                <KbRow
                  key={String(row["id"] ?? i)}
                  table={table}
                  row={row}
                  actions={
                    canEdit ? (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {row["kb_origin"] === "Tenant" ? (
                          <span className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEditing(row)}
                              className="text-xs border border-ink-200 rounded px-2 py-1 bg-white hover:border-accent hover:text-accent"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => { setDeleteError(null); setConfirmDelete(row); }}
                              className="text-xs border border-ink-200 rounded px-2 py-1 bg-white text-risk-critical hover:border-risk-critical"
                            >
                              Delete
                            </button>
                          </span>
                        ) : (
                          // System KB content is immutable, so there is nothing
                          // to offer here — say why rather than leaving it blank.
                          <span
                            className="text-[10px] uppercase tracking-wide text-ink-300"
                            title="Shipped with the platform — read-only"
                          >
                            System
                          </span>
                        )}
                      </td>
                    ) : undefined
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Per-table header + row rendering
// =============================================================================

function KbTableHeader({ table, showActions }: { table: KbTable; showActions?: boolean }) {
  const headers: Record<KbTable, string[]> = {
    kb_canonical_algorithm: [
      "Name",
      "Family",
      "Purpose",
      "PQC",
      "Deprecated",
      "Standard reference",
    ],
    kb_algorithm_alias: ["Alias (normalised)", "Resolves to"],
    kb_keys: [
      "Algorithm",
      "Key size",
      "Curve",
      "Quantum vuln.",
      "Risk",
      "Source citation",
    ],
    kb_cert_signature_algm: [
      "Algorithm",
      "Key length",
      "Curve",
      "Quantum vuln.",
      "Risk",
      "Source citation",
    ],
    kb_cert_hashing_algm: [
      "Algorithm",
      "Hash length",
      "Quantum vuln.",
      "Risk",
      "Source citation",
    ],
    kb_cert_public_key_algm: [
      "Algorithm",
      "Key length",
      "Curve",
      "Quantum vuln.",
      "Risk",
      "Source citation",
    ],
    kb_protocol: [
      "Family",
      "Version",
      "Deprecated",
      "Quantum vuln.",
      "Risk",
      "Source citation",
    ],
    kb_cipher_suite: [
      "IANA name",
      "IANA code",
      "Bulk crypto",
      "MAC/AEAD",
      "Risk",
      "Source citation",
    ],
    kb_library: [
      "Library",
      "Vendor",
      "Version",
      "FIPS",
      "PQC",
      "EOL",
      "Risk",
      "Source citation",
    ],
    kb_store: ["Type", "Vendor", "FIPS", "PQC", "Risk", "Source citation"],
    kb_signature: [
      "Purpose",
      "Algorithm",
      "Length",
      "Curve",
      "Risk",
      "Source citation",
    ],
  };
  return (
    <tr>
      {headers[table].map((h) => (
        <th key={h} className="text-left px-3 py-2 font-medium">
          {h}
        </th>
      ))}
      {showActions && (
        <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Actions</th>
      )}
    </tr>
  );
}

function KbRow({
  table,
  row,
  actions,
}: {
  table: KbTable;
  row: Record<string, unknown>;
  /**
   * Trailing <td> holding this row's Edit/Delete controls. Passed in rather
   * than built here so this stays a pure renderer and each of the 11
   * per-table layouts below simply appends it.
   */
  actions?: ReactNode;
}) {
  const tdCls = "px-3 py-2 text-ink";
  const monoCls = "px-3 py-2 font-mono text-xs";
  const cite = (
    <td className="px-3 py-2 text-xs text-ink-500 italic max-w-md">
      {(row.source_citation as string) ?? "—"}
    </td>
  );
  const risk = (
    <td className="px-3 py-2">
      <RiskBadge level={row.risk_level as RiskLevel} size="xs" />
    </td>
  );
  const qv = (
    <td className="px-3 py-2 text-xs">
      <span className="font-mono">{row.quantum_vulnerability as string}</span>
    </td>
  );

  switch (table) {
    case "kb_canonical_algorithm":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.canonical_name as string}</td>
          <td className={tdCls}>{row.family as string}</td>
          <td className={tdCls}>{(row.primary_purpose as string) ?? "—"}</td>
          <td className="px-3 py-2 text-xs">
            {(row.is_pqc as number) ? "✓" : "—"}
          </td>
          <td className="px-3 py-2 text-xs">
            {(row.is_deprecated as number) ? "✓" : "—"}
          </td>
          <td className="px-3 py-2 text-xs text-ink-500 italic">
            {row.standard_reference as string}
          </td>
          {actions}
        </tr>
      );
    case "kb_algorithm_alias":
      return (
        <tr className="hover:bg-ink-50">
          <td className={monoCls}>{row.alias_text as string}</td>
          <td className={tdCls}>{row.canonical_name as string}</td>
          {actions}
        </tr>
      );
    case "kb_keys":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.canonical_name as string}</td>
          <td className={monoCls}>{(row.key_size as number) ?? "—"}</td>
          <td className={monoCls}>{(row.curve_name as string) ?? "—"}</td>
          {qv}
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_cert_signature_algm":
    case "kb_cert_public_key_algm":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.canonical_name as string}</td>
          <td className={monoCls}>{(row.key_length as number) ?? "—"}</td>
          <td className={monoCls}>{(row.curve_name as string) ?? "—"}</td>
          {qv}
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_cert_hashing_algm":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.canonical_name as string}</td>
          <td className={monoCls}>{(row.hash_length as number) ?? "—"}</td>
          {qv}
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_protocol":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.protocol_family as string}</td>
          <td className={monoCls}>{row.protocol_version as string}</td>
          <td className="px-3 py-2 text-xs">
            {(row.is_deprecated as number) ? "✓" : "—"}
          </td>
          {qv}
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_cipher_suite":
      return (
        <tr className="hover:bg-ink-50">
          <td
            className={`${monoCls} truncate max-w-md`}
            title={row.iana_name as string}
          >
            {row.iana_name as string}
          </td>
          <td className={monoCls}>{(row.iana_code as string) ?? "—"}</td>
          <td className={monoCls}>
            {(row.bulk_encryption_algorithm as string) ?? "—"}
          </td>
          <td className={monoCls}>{(row.mac_or_aead as string) ?? "—"}</td>
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_library":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.name as string}</td>
          <td className={tdCls}>{(row.vendor as string) ?? "—"}</td>
          <td className={monoCls}>{row.version_pattern as string}</td>
          <td className="px-3 py-2 text-xs">
            {(row.is_fips_validated as number) ? "✓" : "—"}
          </td>
          <td className="px-3 py-2 text-xs">
            {(row.has_pqc_support as number) ? "✓" : "—"}
          </td>
          <td className="px-3 py-2 text-xs">
            {(row.end_of_life_date as string) ?? "—"}
          </td>
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_store":
      return (
        <tr className="hover:bg-ink-50">
          <td className={tdCls}>{row.store_type as string}</td>
          <td className={tdCls}>{(row.vendor as string) ?? "—"}</td>
          <td className="px-3 py-2 text-xs">{row.fips_level as string}</td>
          <td className="px-3 py-2 text-xs">
            {(row.has_pqc_support as number) ? "✓" : "—"}
          </td>
          {risk}
          {cite}
          {actions}
        </tr>
      );
    case "kb_signature":
      return (
        <tr className="hover:bg-ink-50">
          <td className="px-3 py-2 text-xs">
            {row.signature_purpose as string}
          </td>
          <td className={monoCls}>{(row.canonical_name as string) ?? "—"}</td>
          <td className={monoCls}>{(row.key_length as number) ?? "—"}</td>
          <td className={monoCls}>{(row.curve_name as string) ?? "—"}</td>
          {risk}
          {cite}
          {actions}
        </tr>
      );
  }
}
